const LEGACY_ABSENCE_FIELDS = [
  "personalHolidays",
  "illnessAccidentDoctor",
  "militaryCivilService",
  "maternityPaternityLeave",
  "publicOffices",
  "vocationalSchool",
];

const NON_ORDER_RELATED_FIELDS = [
  "educationTraining",
  "advertisingAcquisition",
  "contest",
  "managementLeadership",
  "edvOperation",
  "others",
];

const LEGACY_EMPLOYEE_FIELDS = ["varBenefits", "vacation", "overUnderTime", ...LEGACY_ABSENCE_FIELDS];
const hasOwn = (value, field) => Object.prototype.hasOwnProperty.call(value || {}, field);
const hasValue = (value) => value !== null && value !== undefined && value !== "";
const toNumber = (value) => {
  if (!hasValue(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const sumFields = (employee, fields) => {
  const values = fields.map((field) => toNumber(employee?.[field])).filter((value) => value != null);
  return values.length ? values.reduce((total, value) => total + value, 0) : null;
};
const sumWithZeroFallback = (employee, fields) => sumFields(employee, fields) ?? 0;
const optionLabel = (value) => value?.label ?? value?.name ?? value?.value ?? value?.id ?? value ?? "";

export const isLegacyEmployeeSnapshot = (employee) => LEGACY_EMPLOYEE_FIELDS.some((field) => hasOwn(employee, field));

export const prepareHistoricalEmployeeRow = (employee) => {
  const legacy = isLegacyEmployeeSnapshot(employee);
  const absence = legacy ? sumWithZeroFallback(employee, LEGACY_ABSENCE_FIELDS) : null;
  const nominalTime = toNumber(employee?.yearlyNominalTime ?? employee?.nominalAttendanceTime);
  const storedEffectiveTime = toNumber(employee?.yearlyPresenceTime);
  const effectiveTime =
    legacy && nominalTime != null
      ? nominalTime - (toNumber(employee?.vacation) ?? 0) + (toNumber(employee?.overUnderTime) ?? 0) - absence
      : storedEffectiveTime;
  const nonOrderRelatedAttendance = sumWithZeroFallback(employee, NON_ORDER_RELATED_FIELDS);
  const legacyGrossSalary = sumFields(employee, ["baseSalary", "addBenefits", "varBenefits"]);
  const currentGrossSalary = toNumber(employee?.grossSalary) ?? sumFields(employee, ["baseSalary", "addBenefits"]);

  return {
    id: String(employee?.id ?? employee?.employeeId ?? ""),
    nameOrNum: employee?.nameOrNum ?? "",
    specXFunc: optionLabel(employee?.specXFunc),
    employmentGrade: toNumber(employee?.employmentGrade),
    yearOfBirth: toNumber(employee?.yearOfBirth),
    grossSalary: legacy ? legacyGrossSalary : currentGrossSalary,
    absence,
    effectiveTime,
    nonOrderRelatedAttendance,
    orderRelatedAttendance: effectiveTime == null ? null : effectiveTime - nonOrderRelatedAttendance,
    remarks: employee?.remarks ?? "",
    legacy,
  };
};

export const prepareHistoricalEmployeeRows = (employees = []) => employees.map(prepareHistoricalEmployeeRow);

export const hasLegacyEmployeeRows = (rows = []) => rows.some((row) => row.legacy);

export const prepareHistoricalEmployeesForTable = (employees = []) =>
  employees.map((employee) => {
    const row = prepareHistoricalEmployeeRow(employee);
    return {
      ...employee,
      grossSalary: row.grossSalary,
      absence: row.absence,
      yearlyPresenceTime: row.effectiveTime,
      effectiveTime: row.effectiveTime,
      orderRelatedAttendance: row.orderRelatedAttendance,
      nonOrderRelatedAttendance: row.nonOrderRelatedAttendance,
    };
  });

const isZero = (value) => value != null && Number.isFinite(Number(value)) && Number(value) === 0;

export const prepareLegacyOverheads = (response = [], translate = (key) => key) => {
  const overheads = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
  return overheads.map((overhead) => {
    const content = (Array.isArray(overhead?.children) ? overhead.children : []).map((child) => ({
      accountId: child?.account?.id,
      amount: child?.amount,
      code: child?.account?.code,
      id: child?.account?.id,
      label: child?.account?.name,
      lastUpdated: child?.lastUpdated,
      parentAccountId: child?.account?.parentAccountId,
    }));
    const accountId = overhead?.account?.id ?? overhead?.accountId;
    return {
      id: accountId,
      title: overhead?.account?.name,
      tooltip:
        typeof accountId === "number" && accountId >= 1 && accountId <= 15
          ? translate(`benchmarking.survey.company-overhead.accordion.tooltip.${accountId}`)
          : "",
      skipField: content.every((child) => isZero(child.amount)),
      total: overhead?.amount,
      content,
      errors: [],
    };
  });
};
